import { getCurrentCustomerDetails, getCurrentCustomerId } from 'src/app/customers/state/customersSelector';
import { useAppDispatch, useAppSelector } from 'src/store';
import { useSearchParams, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useProjectsQuery } from 'src/app/projects/views/project-list/state/api/projectListGraphSlice';
import useAuthService from 'src/shared/hooks/useAuthService';
import useAppNavigation from 'src/shared/hooks/useAppNavigation';
import { resetPortfolioFilters } from 'src/app/portfolio/state/portfolioGeneralSlice';
import { clearAllGnrState } from 'src/app/portfolio/views/global-nature-risk/state/gnrFiltersSlice';
import { setCurrentProjectId } from 'src/app/projects/views/project-list/state/projectListSlice';
import { useLazyGetProductLicensingQuery } from 'src/app/admin/views/risk-portfolio/state/api/organisationEngineSlice';
import { ProductKey, ProductVisibility } from 'src/shared/types/engine/products';
import { defaultCustomerId, isGnpShown, landingPage, landingPath } from '../landingPage';
import { setCurrentCustomerId as setCurrentCustomerIdAction } from '../state/customersSlice';
import { useCustomersQuery } from '../state/api/customersGraphSlice';

/** The Global Nature Risk pages are under `/portfolio` too, but they are not Global Nature Performance. */
const isGnpPage = (pathname: string) => pathname.startsWith('/portfolio') && !pathname.startsWith('/portfolio/global-nature-risk');

const useCustomer = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { pathname } = useLocation();
    const customerIdFromUrl = searchParams.get('customerId');

    const customerIdInStore = useAppSelector(getCurrentCustomerId);
    const currentCustomerId = customerIdInStore || customerIdFromUrl || '';
    const currentCustomerDetails = useAppSelector(getCurrentCustomerDetails);
    const { currentData: allCustomers } = useCustomersQuery({});

    const currentCustomerName = allCustomers?.customers.find(customer => customer.customerId === currentCustomerId)?.customerName || '';
    const currentCustomerHasPortfolio =
        allCustomers?.customers.find(customer => customer.customerId === currentCustomerId)?.hasPortfolio || false;
    const dispatch = useAppDispatch();
    const { isAuthenticated, permissions } = useAuthService();
    const navigate = useAppNavigation();
    const [triggerGetProductLicensing] = useLazyGetProductLicensingQuery();

    const {
        isFetching: isFetchingCurrentCustomerProjects,
        currentData: currentCustomerProjects,
        error,
    } = useProjectsQuery(
        {
            customerId: currentCustomerId || '',
        },
        {
            skip: !currentCustomerId || !isAuthenticated,
            refetchOnFocus: true,
            refetchOnReconnect: true,
        }
    );

    // The URL is the source of truth: changing organisation writes the URL, and the store follows it.
    // React Router 7 applies URL updates in a transition, so a store that changed first would be
    // pulled back to the organisation the URL still names.
    useEffect(() => {
        if (customerIdFromUrl && customerIdFromUrl !== customerIdInStore) {
            dispatch(setCurrentCustomerIdAction(customerIdFromUrl));
        } else if (!currentCustomerId && allCustomers?.customers && allCustomers.customers.length > 0 && isAuthenticated) {
            setCurrentCustomerId(defaultCustomerId(allCustomers.customers) as string);
        }
    }, [customerIdFromUrl, customerIdInStore, currentCustomerId, allCustomers, isAuthenticated]);

    // Don't throw for transient network/api errors – allow UI to continue and auto-refetch on focus/reconnect
    if (error) {
        console.warn('Projects list fetch error (non-fatal):', error);
    }

    const setCurrentCustomerId = (customerId: string) => {
        // The store follows the URL (see the effect above). Drop any projectId param too — it belongs to the previous
        // customer, and leaving it in the URL would let useProject()'s URL-sync effect resurrect it
        // into currentProjectId before navigation away from a project-scoped page takes effect.
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set('customerId', customerId);
        newSearchParams.delete('projectId');
        setSearchParams(newSearchParams);
    };

    const currentCustomerProjectIds = currentCustomerProjects?.map(project => project.projectId) || [];

    // Explicit user-initiated org switch. The customer-scoped filters and the selected project are
    // reset: currentProjectId persists in storage, so it would keep the Nature Performance sidebar
    // items pointing at a project of the old org. Global Nature Risk counts only when it is Visible:
    // being known to the engine is not enough, because every customer is onboarded into the engine.
    const switchCustomer = async (customerId: string) => {
        if (customerId === currentCustomerId) {
            return;
        }

        const wasOnGnpPage = isGnpPage(pathname);

        dispatch(resetPortfolioFilters());
        dispatch(clearAllGnrState());
        dispatch(setCurrentProjectId(null));
        setCurrentCustomerId(customerId);

        const { data: productLicensing } = await triggerGetProductLicensing(customerId);
        const isGnrVisible = productLicensing?.[ProductKey.GLOBAL_NATURE_RISK] === ProductVisibility.VISIBLE;
        const hasPortfolio = isGnpShown({
            hasPortfolio: allCustomers?.customers.find(customer => customer.customerId === customerId)?.hasPortfolio ?? false,
            setting: productLicensing?.[ProductKey.GLOBAL_NATURE_PERFORMANCE],
            isInternal: permissions?.is_internal_user ?? false,
        });

        navigate.to(landingPath(landingPage({ hasPortfolio, isGnrVisible, wasOnGnpPage }), customerId));
    };

    return {
        allCustomers,
        currentCustomerId,
        currentCustomerDetails,
        currentCustomerName,
        currentCustomerHasPortfolio,
        setCurrentCustomerId,
        switchCustomer,
        currentCustomerProjects,
        isFetchingCurrentCustomerProjects,
        currentCustomerProjectIds,
        error: error as unknown as Error,
    };
};

export default useCustomer;
